// src/app/pages/auth/RegisterPage.jsx
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { register } from "../../features/auth/authApi.js";

export default function RegisterPage() {
  const nav = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [accountId, setAccountId] = useState("");

  const passwordStrength = useMemo(() => {
    const p = form.password || "";
    let score = 0;
    if (p.length >= 8) score += 30;
    if (p.length >= 12) score += 10;
    if (/[a-z]/.test(p)) score += 15;
    if (/[A-Z]/.test(p)) score += 15;
    if (/[0-9]/.test(p)) score += 15;
    if (/[^A-Za-z0-9]/.test(p)) score += 15;
    return Math.min(score, 100);
  }, [form.password]);

  const strengthLabel = useMemo(() => {
    if (!form.password) return "";
    if (passwordStrength < 45) return "Weak";
    if (passwordStrength < 75) return "Fair";
    return "Strong";
  }, [form.password, passwordStrength]);

  const strengthClass = useMemo(() => {
    if (!form.password) return "bg-slate-700";
    if (passwordStrength < 45) return "bg-red-500";
    if (passwordStrength < 75) return "bg-amber-500";
    return "bg-emerald-500";
  }, [form.password, passwordStrength]);

  const onChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validate = () => {
    const email = form.email.trim();
    if (!email) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address.";
    if (!form.password) return "Password is required.";
    if (form.password.length < 8) return "Password must be at least 8 characters.";
    if (form.password !== form.confirmPassword) return "Passwords do not match.";
    if (!form.agreeToTerms) return "You must agree to the terms and conditions.";
    return "";
  };

  const copyAccountId = async () => {
    try {
      await navigator.clipboard.writeText(accountId);
      setSuccess("Account created. Account ID copied to clipboard.");
    } catch {
      setSuccess("Account created. (Copy failed — please copy manually.)");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setAccountId("");

    try {
      // 1) Register
      const regRes = await register({ email: form.email.trim(), password: form.password });

      // Capture account ID if backend returns it
      const accId = regRes?.account_id || regRes?.accountId || "";
      if (accId) setAccountId(accId);

      setSuccess("Account created! You will be redirected to KYC verification in 3 seconds...");

      // 2) DO NOT auto-login - Redirect to KYC verification after delay
      setTimeout(() => {
        // Store temporary registration data for KYC page
        localStorage.setItem("pending_registration", JSON.stringify({
          email: form.email.trim(),
          accountId: accId,
          timestamp: new Date().toISOString()
        }));
        
        nav("/auth/kyc-verification", { replace: true });
      }, 3000);

    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = err?.message;
      setError(detail || message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-white">Create account</h1>
          <p className="text-sm text-slate-400 mt-1">
            Create your CryptoTradeX account to start trading
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
            {success}
            {accountId && (
              <div className="mt-2 text-xs text-emerald-200">
                Please complete KYC verification to access the platform.
              </div>
            )}
          </div>
        )}

        {/* Account ID (if returned by backend) */}
        {accountId && (
          <div className="mb-4 rounded-lg border border-indigo-500/30 bg-indigo-500/10 p-3">
            <div className="text-xs text-indigo-200">Your Account ID (save this for login)</div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="font-mono text-sm text-white break-all">{accountId}</div>
              <button
                type="button"
                onClick={copyAccountId}
                className="shrink-0 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-xs px-3 py-1.5"
              >
                Copy
              </button>
            </div>
            <div className="mt-2 text-xs text-amber-300">
              ⚠️ Important: You must complete KYC verification before you can trade.
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={onChange("email")}
              placeholder="you@example.com"
              className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoComplete="email"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={onChange("password")}
                placeholder="Minimum 8 characters"
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 pr-16 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoComplete="new-password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white px-2 py-1"
                disabled={loading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            {form.password && (
              <div className="mt-2">
                <div className="h-2 w-full rounded bg-slate-800 overflow-hidden border border-white/5">
                  <div className={`h-full ${strengthClass}`} style={{ width: `${passwordStrength}%` }} />
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  Password strength: <span className="text-slate-200">{strengthLabel}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">Confirm password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={form.confirmPassword}
              onChange={onChange("confirmPassword")}
              placeholder="Re-enter password"
              className={`w-full rounded-lg bg-slate-800 border px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                form.confirmPassword && form.password !== form.confirmPassword
                  ? "border-red-500/60"
                  : "border-slate-700"
              }`}
              autoComplete="new-password"
              required
              disabled={loading}
            />
            {form.confirmPassword && form.password !== form.confirmPassword && (
              <div className="mt-1 text-xs text-red-300">Passwords do not match.</div>
            )}
          </div>

          <label className="flex items-start gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.agreeToTerms}
              onChange={onChange("agreeToTerms")}
              disabled={loading}
              className="mt-1 h-4 w-4 rounded border-slate-700 bg-slate-800"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="text-indigo-300 hover:text-indigo-200 underline" target="_blank">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-indigo-300 hover:text-indigo-200 underline" target="_blank">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-500 transition font-semibold py-2 text-white disabled:opacity-60"
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        {/* KYC Notice */}
        <div className="mt-6 p-3 rounded-lg bg-amber-900/20 border border-amber-700/30">
          <div className="text-sm text-amber-200 font-semibold mb-1">⚠️ KYC Verification Required</div>
          <div className="text-xs text-amber-100/80">
            To comply with regulations, all users must complete KYC verification before accessing the trading platform.
            You'll be redirected to the verification page immediately after registration.
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-slate-400">
          Already have an account?{" "}
          <Link to="/auth/login" className="text-indigo-400 hover:text-indigo-300 font-medium">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}